import React, {useEffect, useState} from 'react';
import { useDispatch } from 'react-redux';
import styled, {ThemeProvider} from 'styled-components';
import {CopyToClipboard} from 'react-copy-to-clipboard';
import {FormattedMessage} from '@kepler.gl/localization';
import {ActionHandler, setExportHTMLMapMode, setMapInfo} from '@kepler.gl/actions';
import {MapInfo} from '@kepler.gl/types';
import {injectIntl} from 'react-intl';
import {IntlShape} from 'react-intl';
import {Session} from './session-manager';


import {
  StyledExportSection,
  InputLight,
  Button,
  CheckMark,
} from '@kepler.gl/components';

import axios from 'axios';

const host = process.env.API_URL;

interface StyledInputProps {
  error?: boolean;
}

const StyledInput = styled.input<StyledInputProps>`
  width: 100%;
  padding: ${props => props.theme.inputPadding};
  color: ${props => (props.error ? 'red' : props.theme.titleColorLT)};
  height: ${props => props.theme.inputBoxHeight};
  outline: 0;
  font-size: ${props => props.theme.inputFontSize};

  :active,
  :focus,
  &.focus,
  &.active {
    outline: 0;
  }
`;

export const StyledInputLabel = styled.label`
  font-size: 12px;
  color: ${props => props.theme.textColorLT};
  letter-spacing: 0.2px;
`;

export const StyleSharingUrl = styled.div.attrs({
  className: 'sharing-url'
})`
  width: 100%;
  display: flex;
  margin-bottom: 14px;
  flex-direction: column;

  input {
    border-right: 0;
  }

  .button {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
`;

interface SharingUrlProps {
  url: string;
  message?: string;
}

export const SharingUrl: React.FC<SharingUrlProps> = ({url, message = ''}) => {
  const [copied, setCopy] = useState(false);
  return (
    <StyleSharingUrl>
      <StyledInputLabel>{message}</StyledInputLabel>
      <div style={{display: 'flex'}}>
        <InputLight type="text" value={url} readOnly />
        <CopyToClipboard text={url} onCopy={() => setCopy(true)}>
          <Button width="80px">{copied ? 'Copied!' : 'Copy'}</Button>
        </CopyToClipboard>
      </div>
    </StyleSharingUrl>
  );
};

const StyledInnerDiv = styled.div`
  min-height: 500px;
`;

interface SessionManagerModalFactoryProps {
  session: Session;
  onGenerateSessionData: () => {config: any; datasets: string[]};
  mapUrl: string;
  newSession: boolean;
  onBack: () => void;
  onSave: () => void;
}

type IntlProps = {
  intl: IntlShape;
};

export default function SessionManagerModalFactory() {
  const SessionManagerModal: React.FC<SessionManagerModalFactoryProps & IntlProps> = ({
    session = {} as Session,
    onGenerateSessionData,
    mapUrl,
    newSession,
    onBack = () => null,
    onSave = () => null,
  }) => {
    const [url, setUrl] = useState(mapUrl);
    const [saveError, setSaveError] = useState('');
    const [sessionData, setSessionData] = useState(session || {} as Session);

    useEffect(() => {
      if (Object.keys(sessionData).length === 0) {
        setSessionData(previousSessionData => ({
          ...previousSessionData,
          share_type: 'private'
        }));
      }
    }, [])

    const onChangeInput = (
      key: string,
      {target: {value}}: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
    ) => {
      setSaveError('');
      setSessionData(previousSessionData => ({
        ...previousSessionData,
        [key]: value
      }));
    };

    const saveMapConfig = async (fileName: string, fileData) => {
      const token = sessionStorage.getItem('token');
      await axios.post(`${host}/api/files`,
        {
          'filename': fileName,
          'file_category': 'config'
        }, 
        {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      })
        .then(response => {
          const { url } = response.data.body;
          return fetch(url, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json'
            },
            body: fileData
          });
        })
        .catch(error => {
          console.error(error);
        });
    }

    const saveNewSession = async (mapData) => {
      const configId = `session_config_${sessionData.name}.json`
      try {
        await saveMapConfig(configId, mapData.config)
          .then(() => {
            const payload = {
              ...sessionData,
              name: sessionData.name,
              data_files: mapData.datasets,
              config_id: configId,
              share_type: sessionData.share_type,
              password: sessionData.password ? sessionData.password : '',
            }
            setSessionData(payload);
            const token = sessionStorage.getItem('token');
            return axios.post(`${host}/api/sessions`, payload, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              }});
          });
      } catch (error) {
        console.error('Error saving map:', error);
      }
    };

    const saveExistingSession = async (mapData) => {
      const configId = `session_config_${sessionData.name}.json`
      await saveMapConfig(configId, mapData.config);
      try {
        const payload = {
          ...sessionData,
          name: sessionData.name,
          data_files: mapData.datasets,
          config_id: configId,
          share_type: sessionData.share_type,
          password: sessionData.password ? sessionData.password : '',
        }
        setSessionData(payload);
        const token = sessionStorage.getItem('token');
        await axios.put(`${host}/api/sessions/${sessionData.id}`, payload, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }});
      } catch (error) {
        console.error('Error saving map:', error);
      }
    };

    const handleSave = async () => {
      const mapData = onGenerateSessionData();
      if (!sessionData.name || sessionData.name.trim() === '') {
        setSaveError('Session name must be specified.')
      } else if (sessionData.share_type != 'private' && (!sessionData.password || sessionData.password === '')) {
        setSaveError('Public sessions must have a password.')
      } else if (!mapData.datasets || mapData.datasets.length == 0) {
        setSaveError('No datasets loaded. Saved sessions must have at least one dataset.')
      } else if (newSession) {
        await saveNewSession(mapData);
        onSave();
      } else {
        await saveExistingSession(mapData);
        onSave();
      }
    };

    return (
        <StyledInnerDiv>
            <StyledExportSection>
                <div className="description">
                <div className="title">
                    <FormattedMessage id="Name"/>
                </div>
                <div className="subtitle">
                    <FormattedMessage id={'Only alphanumeric and spaces allowed. Maximum of 32 characters.'} />
                </div>
                </div>
                <div className="selection">
                <StyledInput
                    type="text"
                    value={sessionData.name}
                    onChange={e => onChangeInput('name', e)}
                    placeholder="Type session name"
                    pattern="[a-zA-Z0-9\s]+"
                    maxLength={32}
                />
                </div>
            </StyledExportSection>
            <StyledExportSection>
                <div className="description">
                <div className="title">
                    <FormattedMessage id="Share type"/>
                </div>
                <div className="subtitle">
                    <FormattedMessage id={'Configure how the session is shared. Private means it is not shared. Public means ANYONE with a Vista account can open the session (including loading the associated data).'} />
                </div>
                </div>
                <div className="selection">
                  <label>
                    <input
                      type="radio"
                      checked={sessionData.share_type === 'private'}
                      value='private'
                      onChange={e => onChangeInput('share_type', e)}
                    />
                    Private
                  </label>
                  <label>
                    <input
                      type="radio"
                      checked={sessionData.share_type === 'public'}
                      value='public'
                      onChange={e => onChangeInput('share_type', e)}
                    />
                    Public
                  </label>
                </div>
            </StyledExportSection>
            { sessionData.share_type === 'private' ? null :
              <div>
              <StyledExportSection>
                  <div className="description">
                  <div className="title">
                      <FormattedMessage id="Session password"/>
                  </div>
                  <div className="subtitle">
                      <FormattedMessage id={'Set password for sharing session. Only alphanumeric characters are accepted, up to 32 characters.'} />
                  </div>
                  </div>
                  <div className="selection">
                  <StyledInput
                      type="text" // TODO: Set this as password and include a show password button
                      value={sessionData.password}
                      onChange={e => onChangeInput('password', e)}
                      placeholder=""
                      pattern="[a-zA-Z0-9]+"
                      maxLength={32}
                  />
                  </div>
              </StyledExportSection>
              <StyledExportSection>
                  <div className="description">
                  <div className="title">
                      <FormattedMessage id={'Share session ID'} />
                  </div>
                  <div className="subtitle">
                      <FormattedMessage id={'Share session ID with other Vista users.'} />
                  </div>
                  </div>
                  <div className="selection">
                  {url ? <SharingUrl url={session.id} message="Your session ID"/> : 
                  <div>Available after saving</div>
                  }
                  </div>
              </StyledExportSection>
              </div>
            }
            <StyledExportSection>
              {(saveError ) ? <div style={{ textAlign: 'right', color: 'red' }}>{saveError}</div> : null}
              <div style={{ position: 'absolute', bottom: '20px', right: '20px' }}>
                <Button onClick={onBack} style={{ backgroundColor: 'lightgrey'}}>Cancel</Button>
                <Button onClick={handleSave} style={{ marginLeft: '10px' }}>Save</Button>
              </div>
            </StyledExportSection>
        </StyledInnerDiv>
    );
  };

  return injectIntl(SessionManagerModal);
}
